import { ActivityApplicationConstants } from "@/apps/tatar/activityApp/ActivityHooks";
import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import {
  APOffer,
  APOfferStatus,
} from "@/apps/tatar/activityApp/views/details/offer-feature/APOffer.Interface";
import LoadOverlay from "@/components/LoadOverlay";
import ModalManager from "@/components/ModalComponent/ModalManager";
import i18n from "@/i18n";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import BFMessageBox from "@/modules/abstract-ui/general/Message/BFMessageBox";
import useVacancyApplicantWorkflow from "../../../hooks/useVacancyApplicantWorkflow";
import {
  VacancyApplicant,
  VacancyApplication,
} from "../../../model/VacancyApplicant.interface";
import VacancyOfferUtils from "../../utils/VacancyOfferUtils";
import VacancyApplicantCreditReportPart from "../credit-report/VacancyApplicantCreditReportPart";
import VacancyApplicantEmployerActions from "../vacancy-applicant-entry/VacancyApplicantEmployerActions";
import VacancyApplicantMissingData from "../vacancy-applicant-entry/VacancyApplicantMissingData";

type VacancyApplicantWorkflowSteps =
  | "employmentStatus"
  | "creditWorthiness"
  | "dataMissing";

type Props = {
  activity: APActivity;
  application: VacancyApplication;
  applicant: VacancyApplicant;
  offer: APOffer;
  constants: ActivityApplicationConstants;
  hideActions?: boolean;
};

const VacancyApplicantWorkflowStatus = (props: Props) => {
  const {
    application,
    applicant,
    activity,
    offer,
    constants,
    hideActions = false,
  } = props;

  const { rejectApplicant, loadingReject } = useVacancyApplicantWorkflow({
    activity,
    application,
    offer,
    constants,
  });

  if (!applicant) {
    return <div>-</div>;
  }
  const offerStatus = offer.data.status as APOfferStatus;
  if (offerStatus === "archived") {
    return null;
  }

  const workflowComponents: VacancyApplicantWorkflowSteps[] = [];

  if (applicant.creditWorthiness === undefined) {
    workflowComponents.push("creditWorthiness");
  }

  const level = VacancyOfferUtils.getEmploymentStatusLevel(applicant, activity);
  if (
    (level?.isGuarantorNeeded && !applicant.guarantor) ||
    level?.isRentCoverageNeeded
  ) {
    workflowComponents.push("employmentStatus");
  }

  const isDataMissing =
    applicant.employmentInfo === undefined ||
    applicant.agreeCreditCheck === undefined ||
    applicant.personalId === undefined ||
    (Array.isArray(applicant.personalId) && applicant.personalId.length === 0);

  if (isDataMissing) {
    workflowComponents.push("dataMissing");
  }

  return (
    <div className="padding-top-20 ">
      <LoadOverlay open={loadingReject} />
      {workflowComponents.length > 0 && (
        <>
          <BFMessageBox type="warning" bordered className="__text-break">
            <div className="padding-bottom-10">
              {i18n.t(
                "apTemplate:Vacancy.Applicant.WorkflowSteps.Message",
                "Zu diesem Mietinteressenten sind nicht alle relevanten Daten für eine Freigabe erfasst."
              )}
            </div>

            {!hideActions && (
              <div className="__grid">
                {workflowComponents.includes("creditWorthiness") && (
                  <CreditWorthinessPart
                    application={application}
                    applicant={applicant}
                    constants={constants}
                    editable={true}
                  />
                )}
                {workflowComponents.includes("employmentStatus") && (
                  <VacancyApplicantEmployerActions
                    application={application}
                    applicant={applicant}
                    activity={activity}
                    constants={constants}
                    level={level}
                  />
                )}
              </div>
            )}
            {!hideActions && (
              <div className="__grid">
                {workflowComponents.includes("dataMissing") && (
                  <VacancyApplicantMissingData
                    application={application}
                    applicant={applicant}
                    constants={constants}
                    activity={activity}
                  />
                )}
              </div>
            )}
          </BFMessageBox>
        </>
      )}
      {!hideActions && (
        <div className="__grid">
          {offerStatus === "new" && (
            <div className="__grid">
              <RejectApplicantPart rejectApplicantAction={rejectApplicant} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VacancyApplicantWorkflowStatus;

type CreditWorthinessPartProps = {
  application: VacancyApplication;
  applicant: VacancyApplicant;
  constants: ActivityApplicationConstants;
  editable?: boolean;
};

const CreditWorthinessPart = (props: CreditWorthinessPartProps) => {
  const { application, applicant, constants, editable = false } = props;

  const showCreditWorthinessPart = () => {
    ModalManager.show({
      noPadding: true,
      content: (
        _states: any,
        _setStates: (states: any) => void,
        closeModal?: () => void
      ) => (
        <VacancyApplicantCreditReportPart
          application={application}
          applicant={applicant}
          constants={constants}
          editable={editable}
          onChanged={() => {
            if (closeModal) {
              closeModal();
            }
          }}
        />
      ),
    });
  };

  return (
    <div>
      <div>
        {i18n.t(
          "apTemplate:Vacancy.Applicant.Label.CreditWorthiness.NoScore",
          "Der Schufa Score wurde nicht erfasst."
        )}
      </div>
      <BFButton
        appearance="link"
        onClick={() => {
          showCreditWorthinessPart();
        }}
      >
        {i18n.t(
          "apTemplate:Vacancy.Applicant.Label.CreditWorthiness.EditScore",
          "Schufa Score erfassen"
        )}
      </BFButton>
    </div>
  );
};

type RejectApplicantPartProps = {
  rejectApplicantAction: () => void;
};

const RejectApplicantPart = (props: RejectApplicantPartProps) => {
  const { rejectApplicantAction } = props;

  return (
    <div>
      <BFButton
        appearance="link"
        onClick={() => {
          ModalManager.confirm({
            title: i18n.t(
              "apTemplate:Vacancy.Applicant.Label.RejectApplicant.Title",
              "Mietinteressent absagen"
            ),
            message: i18n.t(
              "apTemplate:Vacancy.Applicant.Label.RejectApplicant.Confirm",
              "Möchten Sie den Mietinteressenten absagen?"
            ),
            onConfirm: () => {
              rejectApplicantAction();
            },
          });
        }}
      >
        {i18n.t(
          "apTemplate:Vacancy.Applicant.Label.RejectApplicant.Btn",
          "Mietinteressent absagen"
        )}
      </BFButton>
    </div>
  );
};
